import { Button, Flex, Form, Tabs, Text } from "../../components";
import { useApp, useForm } from "../../hooks";
import { useEffect, useState } from "react";
import {
  toAreaOptions,
  toBranchOptions,
  toCityOptions,
} from "../../utils/cities";
import { Images } from "../../assets";
import { OrderType } from "../../types/enums";
import { Tools } from "../../utils";

const AddLocation = ({ onSave = async () => {} }) => {
  const {
    cities = [],
    saveLocation,
    location,
    orderOptions,
    logoUrl,
  } = useApp();
  const allowDelivery = !!orderOptions?.allowDelivery;
  const allowPickup = !!orderOptions?.allowPickup;
  const [chosen, setChosen] = useState<OrderType>(
    location?.orderType ||
      (allowDelivery ? OrderType.delivery : OrderType.pickup),
  );
  const orderType =
    allowDelivery && allowPickup
      ? chosen
      : allowPickup
        ? OrderType.pickup
        : OrderType.delivery;
  const isDelivery = orderType === OrderType.delivery;
  const form = useForm({
    initialValues: {
      city: location?.city?.uuid,
      area: location?.area?.uuid,
      branch: location?.branch?.uuid,
    },
  });
  const selectedCity = Form.useWatch("city", form);
  const selectedArea = Form.useWatch("area", form);
  const selectedBranch = Form.useWatch("branch", form);
  const city = cities.find((item) => item.uuid === selectedCity);
  const areas = toAreaOptions(city?.areas ?? []);
  const branches = toBranchOptions(city?.branches ?? []);
  const canSave = isDelivery
    ? !!(selectedCity && selectedArea)
    : !!(selectedCity && selectedBranch);

  useEffect(() => {
    if (!selectedCity && cities.length === 1)
      form.setFieldValue("city", cities[0].uuid);
  }, [cities, form, selectedCity]);

  useEffect(() => {
    if (!selectedCity || !city) return;
    if (isDelivery) {
      const known = areas.some((option) => option.value === selectedArea);
      if (selectedArea && !known) form.setFieldValue("area", undefined);
      else if (!selectedArea && areas.length === 1)
        form.setFieldValue("area", areas[0].value);
    } else {
      const known = branches.some((option) => option.value === selectedBranch);
      if (selectedBranch && !known) form.setFieldValue("branch", undefined);
      else if (!selectedBranch && branches.length === 1)
        form.setFieldValue("branch", branches[0].value);
    }
  }, [
    areas,
    branches,
    city,
    form,
    isDelivery,
    selectedArea,
    selectedBranch,
    selectedCity,
  ]);

  const addLocation = async () => {
    const values = await form.validateFields();
    await saveLocation({
      city: values.city,
      area: isDelivery ? values.area : undefined,
      branch: isDelivery ? undefined : values.branch,
      orderType,
    });
    await onSave();
  };

  return (
    <>
      <Flex vertical align="center" className="m-20-b">
        <img
          alt=""
          src={Tools.mediaUrl(logoUrl) || Images.logo}
          style={{ height: 80, objectFit: "contain" }}
        />
      </Flex>
      {allowDelivery && allowPickup ? (
        <Tabs
          centered
          className="m-30-b mx-[6rem] w-[calc(100%-12rem)] mobile:mx-0 mobile:w-full"
          value={orderType}
          onChange={(next) => {
            setChosen(next);
            form.setFieldValue("area", undefined);
            form.setFieldValue("branch", undefined);
          }}
          items={[
            { value: OrderType.delivery, label: "Delivery" },
            { value: OrderType.pickup, label: "Pickup" },
          ]}
        />
      ) : (
        <Text
          level={3}
          className="m-10-b"
          children={isDelivery ? "Deliver To" : "Pickup From"}
        />
      )}
      <Form
        form={form}
        onValuesChange={(changed) => {
          if (changed.city) {
            form.setFieldValue("area", undefined);
            form.setFieldValue("branch", undefined);
          }
        }}
        fields={[
          {
            rules: [{ required: true }],
            name: "city",
            face: "select",
            placeholder: cities.length ? "Select your city" : "Loading cities…",
            options: toCityOptions(cities),
          },
          ...(selectedCity
            ? [
                {
                  name: isDelivery ? "area" : "branch",
                  label: isDelivery ? "Area" : "Branch",
                  face: "select",
                  showSearch: true,
                  rules: [{ required: true }],
                  placeholder: isDelivery
                    ? "Select your area"
                    : branches.length
                      ? "Select your branch"
                      : "No branches in this city",
                  options: isDelivery ? areas : branches,
                },
              ]
            : []),
        ]}
      />
      <Button
        title="Save"
        className="w-100"
        minWidth="100%"
        style={{ marginTop: 45 }}
        disabled={!canSave}
        onClick={addLocation}
      />
    </>
  );
};

export default AddLocation;
